import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DataTable } from '@repo/videa-components/src/index';
import { Input } from '@repo/videa-components/src/ui/input';
import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { PrivacyButton } from '@/modules/patientList/PrivacyButton';
import type {
  THygieneTaskType,
  THygieneTasksDrilldown,
} from '@/react-query/insights/types';
import { usePreferences } from '@/react-query/usePreferences';
import { sanitizeForFilename } from '@/utils/file';

import { endDateAtom, startDateAtom } from '../../atoms';
import { InsightsExportButton } from '../../components/InsightsExportButton';

import {
  hygieneProviderTableColumnsBase,
  hygieneProviderTableColumnsTypes,
} from './HygieneProviderTableColumns';

export const HygieneProviderTable = ({
  taskDrilldowns,
  isLoading,
  providerName,
}: {
  taskDrilldowns?: THygieneTasksDrilldown[];
  isLoading?: boolean;
  providerName?: string;
}) => {
  const startDate = useAtomValue(startDateAtom);
  const endDate = useAtomValue(endDateAtom);
  const slug =
    providerName ? sanitizeForFilename(providerName) || 'provider' : 'provider';
  const dateRange = `${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}`;
  const exportFilename = `insights-hygiene-ai-recommendations-${slug}-${dateRange}.csv`;

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data: prefs } = usePreferences();

  const columnsEnabled = hygieneProviderTableColumnsTypes.filter((column) =>
    prefs?.insightsEnabledTasks?.includes(column.header as THygieneTaskType),
  );

  const columns = [...hygieneProviderTableColumnsBase, ...columnsEnabled];

  const table = useReactTable({
    data: taskDrilldowns ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    state: {
      sorting,
      columnFilters,
    },
  });
  return (
    <div className="vai-flex vai-flex-col vai-gap-4 vai-w-full">
      <div className="vai-flex vai-flex-row vai-justify-between">
        <span className="vai-typography-large">AI Recommendations</span>
        <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center vai-justify-center">
          <InsightsExportButton
            table={table}
            filename={exportFilename}
            isDisabled={!taskDrilldowns?.length}
          />
          <div className="vai-relative">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-z-10"
            />
            <Input
              placeholder="Search patients"
              value={
                (table.getColumn('patientName')?.getFilterValue() as string) ??
                ''
              }
              onChange={(event) =>
                table
                  .getColumn('patientName')
                  ?.setFilterValue(event.target.value)
              }
              className="vai-w-full vai-pl-10 vai-pr-8"
            />
          </div>
          <PrivacyButton />
        </div>
      </div>
      <DataTable
        table={table}
        className="vai-h-[calc(100vh-400px)]"
        isLoading={isLoading}
      />
    </div>
  );
};
